import React from 'react'
import { 
  Card, 
  Button, 
  Space, 
  Typography, 
  Divider, 
  Collapse,
  Slider,
  ColorPicker,
  Select,
  InputNumber,
  Switch,
  Alert,
  Form,
  Modal,
  List
} from 'antd'
import { 
  DeleteOutlined, 
  UndoOutlined, 
  RedoOutlined,
  SettingOutlined,
  HomeOutlined,
  BorderOutlined,
  DragOutlined,
  CloseOutlined
} from '@ant-design/icons'
import { useDesignStore, type DesignObject, type ADUModel, type PoolModel, type PoolShape, type PoolEquipmentModel, type PoolEquipmentType, type ADUEquipmentModel, type ADUEquipmentType } from '../../store/designStore'
import type { Color } from 'antd/es/color-picker'
import { ADU_TEMPLATES } from '../../data/aduTemplates'

const { Title, Text } = Typography
const { Panel } = Collapse

// Draggable Equipment Button Component
interface DraggableEquipmentButtonProps {
  equipmentType: ADUEquipmentType | PoolEquipmentType
  componentType: 'adu' | 'pool'
  label: string
  onClick: () => void
  onDragStart: (equipmentType: ADUEquipmentType | PoolEquipmentType, type: 'adu' | 'pool') => (e: React.DragEvent) => void
  onDragEnd: () => void
  draggedComponent: { type: 'adu' | 'pool', equipmentType: ADUEquipmentType | PoolEquipmentType } | null
}

const DraggableEquipmentButton: React.FC<DraggableEquipmentButtonProps> = ({
  equipmentType,
  componentType,
  label,
  onClick,
  onDragStart,
  onDragEnd,
  draggedComponent
}) => (
  <Button 
    size="small" 
    onClick={onClick}
    icon={<DragOutlined />}
    draggable
    onDragStart={onDragStart(equipmentType, componentType)}
    onDragEnd={onDragEnd}
    style={{ 
      cursor: 'grab',
      opacity: draggedComponent?.equipmentType === equipmentType ? 0.5 : 1 
    }}
    title="Click to add or drag to place"
  >
    {label}
  </Button>
)

interface DesignToolsPanelProps {
  onClose: () => void
}

const DesignToolsPanel: React.FC<DesignToolsPanelProps> = ({ onClose }) => {
  const { 
    selectedObjects, 
    updateObjectProperties, 
    updateADUDimensions, 
    updatePoolDimensions, 
    deleteObject, 
    undo, 
    redo, 
    clearSelection,
    addPoolEquipment,
    addADUEquipment,
    poolModels,
    getPoolEquipmentForPool,
    getADUEquipmentForADU,
    addADUSection,
    updateADUSection,
    deleteADUSection,
    toggleADUModular
  } = useDesignStore()
  const selectedObject = selectedObjects.length > 0 ? selectedObjects[0] : null
  const [form] = Form.useForm()
  const [lastSelectedObjectId, setLastSelectedObjectId] = React.useState<string | null>(null)
  
  // Local state for dimension inputs to prevent reset issues
  const [localDimensions, setLocalDimensions] = React.useState<any>(null)
  const [showTemplateModal, setShowTemplateModal] = React.useState(false)
  
  // Drag and drop state
  const [draggedComponent, setDraggedComponent] = React.useState<{
    type: 'adu' | 'pool',
    equipmentType: ADUEquipmentType | PoolEquipmentType
  } | null>(null)

  // Initialize local dimensions when selectedObject is available
  React.useEffect(() => {
    if (selectedObject && !localDimensions) {
      console.log('Initializing local dimensions:', selectedObject.dimensions)
      setLocalDimensions(selectedObject.dimensions)
    }
  }, [selectedObject, localDimensions])

  // Force re-render when object properties change (for slider updates during drag)
  React.useEffect(() => {
    if (selectedObject) {
      console.log('🔄 Object properties changed:', {
        position: selectedObject.position,
        rotation: selectedObject.rotation,
        scale: selectedObject.scale
      })
    }
  }, [selectedObject?.position, selectedObject?.rotation, selectedObject?.scale])

  React.useEffect(() => {
    if (selectedObject && selectedObject.id !== lastSelectedObjectId) {
      // Only reset form when switching to a different object
      form.setFieldsValue({
        name: selectedObject.name,
        positionX: selectedObject.position[0],
        positionY: selectedObject.position[1],
        positionZ: selectedObject.position[2],
        rotationX: selectedObject.rotation[0],
        rotationY: selectedObject.rotation[1],
        rotationZ: selectedObject.rotation[2],
        scaleX: selectedObject.scale[0],
        scaleY: selectedObject.scale[1],
        scaleZ: selectedObject.scale[2],
      })
      
      // Initialize local dimensions when switching objects
      setLocalDimensions(selectedObject.dimensions)
      setLastSelectedObjectId(selectedObject.id)
      
      console.log('Selected object changed:', {
        id: selectedObject.id,
        type: selectedObject.type,
        dimensions: selectedObject.dimensions
      })
    }
  }, [selectedObject, form, lastSelectedObjectId])

  const handlePropertyChange = (property: string, value: any) => {
    if (!selectedObject) return

    console.log('🎛️ SLIDER/PROPERTY CHANGE:', { property, value, objectId: selectedObject.id })

    let updateData: Partial<DesignObject> = {}

    // Handle position updates
    if (property.startsWith('position')) {
      const axis = property.replace('position', '').toLowerCase()
      const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2
      const newPosition = [...selectedObject.position] as [number, number, number]
      newPosition[axisIndex] = value
      updateData.position = newPosition
    }
    // Handle rotation updates
    else if (property.startsWith('rotation')) {
      const axis = property.replace('rotation', '').toLowerCase()
      const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2
      const newRotation = [...selectedObject.rotation] as [number, number, number]
      newRotation[axisIndex] = value
      updateData.rotation = newRotation
    }
    // Handle scale updates
    else if (property.startsWith('scale')) {
      const axis = property.replace('scale', '').toLowerCase()
      const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2
      const newScale = [...selectedObject.scale] as [number, number, number]
      newScale[axisIndex] = value
      updateData.scale = newScale
    }
    // Handle other properties
    else {
      updateData = { [property]: value }
    }

    updateObjectProperties(selectedObject.id, updateData)
    console.log('🎛️ UPDATE SENT TO STORE:', { objectId: selectedObject.id, updateData })
  }

  const handleColorChange = (materialType: string, color: Color) => {
    if (!selectedObject) return
    
    console.log('Color change:', { materialType, color: color.toHexString() })
    
    const newMaterials = {
      ...selectedObject.materials,
      [materialType]: color.toHexString()
    }
    
    if (selectedObject.type === 'ADU') {
      updateObjectProperties(selectedObject.id, { materials: newMaterials } as Partial<ADUModel>)
    } else if (selectedObject.type === 'Pool') {
      updateObjectProperties(selectedObject.id, { materials: newMaterials } as Partial<PoolModel>)
    }
  }

  const handleMaterialChange = (materialType: string, value: string) => {
    if (!selectedObject) return
    
    console.log('Material change:', { materialType, value })
    
    const newMaterials = {
      ...selectedObject.materials,
      [materialType]: value
    }
    
    if (selectedObject.type === 'ADU') {
      updateObjectProperties(selectedObject.id, { materials: newMaterials } as Partial<ADUModel>)
    } else if (selectedObject.type === 'Pool') {
      updateObjectProperties(selectedObject.id, { materials: newMaterials } as Partial<PoolModel>)
    }
  }

  const handleDimensionChange = (dimension: string, value: number) => {
    if (!selectedObject) return

    console.log('Dimension change:', { dimension, value })

    // Ensure we have local dimensions initialized
    const currentDimensions = localDimensions || selectedObject.dimensions
    
    // Update local state immediately for responsive UI (this is what the input displays)
    const newLocalDimensions = { ...currentDimensions, [dimension]: value }
    setLocalDimensions(newLocalDimensions)

    console.log('Local dimensions updated to:', newLocalDimensions)

    // Update the store (this updates the 3D model)
    if (selectedObject.type === 'ADU') {
      updateADUDimensions(selectedObject.id, { [dimension]: value })
    } else if (selectedObject.type === 'Pool') {
      updatePoolDimensions(selectedObject.id, { [dimension]: value })
    }
  }

  const handleFeatureChange = (feature: string, value: any) => {
    if (!selectedObject || (selectedObject.type !== 'ADU' && selectedObject.type !== 'Pool')) return
    
    console.log('Feature change:', { feature, value })
    
    const newFeatures = {
      ...selectedObject.features,
      [feature]: value
    }
    
    if (selectedObject.type === 'ADU') {
      updateObjectProperties(selectedObject.id, { features: newFeatures } as Partial<ADUModel>)
    } else if (selectedObject.type === 'Pool') {
      updateObjectProperties(selectedObject.id, { features: newFeatures } as Partial<PoolModel>)
    }
  }

  const handleShapeChange = (shape: PoolShape) => {
    if (!selectedObject || selectedObject.type !== 'Pool') return
    
    console.log('Shape change:', { shape })
    updateObjectProperties(selectedObject.id, { shape } as Partial<PoolModel>)
  }

  const handleApplyTemplate = (templateId: string) => {
    if (!selectedObject || selectedObject.type !== 'ADU') return
    
    const template = ADU_TEMPLATES.find(t => t.id === templateId)
    if (template) {
      console.log('Applying template:', template.name)
      
      // Convert template structure to ADU model structure
      const newDimensions = {
        width: template.totalSize.width,
        height: template.totalSize.height,
        depth: template.totalSize.depth
      }
      
      const newFeatures = {
        bedrooms: template.rooms.bedrooms,
        bathrooms: template.rooms.bathrooms,
        kitchen: template.rooms.hasKitchen,
        livingRoom: template.rooms.hasLivingRoom,
        dining: template.rooms.hasDining,
        office: template.rooms.hasOffice,
        storage: template.rooms.hasStorage
      }
      
      updateObjectProperties(selectedObject.id, {
        templateId: template.id,
        name: template.name,
        dimensions: newDimensions,
        features: newFeatures
      } as Partial<ADUModel>)
      
      // Update local dimensions to match template
      setLocalDimensions(newDimensions)
      setShowTemplateModal(false)
    }
  }

  const handleAddPoolEquipment = (equipmentType: PoolEquipmentType) => {
    if (!selectedObject || selectedObject.type !== 'Pool') return
    
    // Get equipment dimensions based on type
    const getEquipmentDimensions = (type: PoolEquipmentType) => {
      switch (type) {
        case 'pump':
          return { width: 2, height: 1.5, depth: 1.5 }
        case 'filter':
          return { width: 2.5, height: 4, depth: 2.5 }
        case 'heater':
          return { width: 3, height: 2.5, depth: 2 }
        case 'electrical_panel':
          return { width: 1.5, height: 2, depth: 0.5 }
        case 'chlorinator':
          return { width: 1.5, height: 2, depth: 1 }
        case 'automation_controller':
          return { width: 1, height: 1.5, depth: 0.3 }
        default:
          return { width: 1, height: 1, depth: 1 }
      }
    }

    // Position equipment near the selected pool
    const poolPosition = selectedObject.position
    const equipmentPosition: [number, number, number] = [
      poolPosition[0] + selectedObject.dimensions.width/2 + 3, // 3 feet from pool edge
      0.5, // Slightly above ground
      poolPosition[2]
    ]

    const equipmentName = equipmentType.split('_').map((word: string) => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')

    addPoolEquipment({
      type: 'PoolEquipment',
      name: `${equipmentName}`,
      equipmentType,
      position: equipmentPosition,
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      dimensions: getEquipmentDimensions(equipmentType),
      materials: {
        primary: '#4a5568',
        secondary: '#2d3748',
        accent: '#718096'
      },
      poolId: selectedObject.id
    })
  }

  const handleAddADUEquipment = (equipmentType: ADUEquipmentType) => {
    if (!selectedObject || selectedObject.type !== 'ADU') return
    
    // Get equipment dimensions based on type
    const getEquipmentDimensions = (type: ADUEquipmentType) => {
      switch (type) {
        // Plumbing fixtures
        case 'toilet':
          return { width: 1.5, height: 2.5, depth: 2.5 }
        case 'sink_bathroom':
          return { width: 2, height: 1, depth: 1.5 }
        case 'sink_kitchen':
          return { width: 3, height: 1, depth: 2 }
        case 'shower':
          return { width: 3, height: 7, depth: 3 }
        case 'bathtub':
          return { width: 5, height: 1.5, depth: 2.5 }
        
        // Kitchen appliances
        case 'refrigerator':
          return { width: 2.5, height: 6, depth: 2.5 }
        case 'stove':
          return { width: 2.5, height: 3, depth: 2 }
        case 'dishwasher':
          return { width: 2, height: 3, depth: 2 }
        case 'microwave':
          return { width: 2, height: 1, depth: 1.5 }
        case 'washing_machine':
          return { width: 2.5, height: 3.5, depth: 2.5 }
        case 'dryer':
          return { width: 2.5, height: 3.5, depth: 2.5 }
        
        // Cabinets & storage
        case 'kitchen_cabinets':
          return { width: 4, height: 3, depth: 2 }
        case 'bathroom_vanity':
          return { width: 3, height: 3, depth: 2 }
        case 'closet_system':
          return { width: 6, height: 8, depth: 2 }
        case 'pantry':
          return { width: 3, height: 8, depth: 2 }
        case 'linen_cabinet':
          return { width: 2, height: 6, depth: 1.5 }
        
        // Windows & doors
        case 'window_standard':
          return { width: 3, height: 4, depth: 0.5 }
        case 'window_large':
          return { width: 6, height: 4, depth: 0.5 }
        case 'sliding_door':
          return { width: 6, height: 8, depth: 0.5 }
        case 'entry_door':
          return { width: 3, height: 8, depth: 0.5 }
        case 'interior_door':
          return { width: 2.5, height: 8, depth: 0.3 }
        
        // HVAC & utilities
        case 'hvac_unit':
          return { width: 3, height: 3, depth: 3 }
        case 'electrical_panel':
          return { width: 1.5, height: 2, depth: 0.5 }
        case 'water_heater':
          return { width: 2, height: 4, depth: 2 }
        
        // Lighting & electrical
        case 'ceiling_light':
          return { width: 1, height: 0.5, depth: 1 }
        case 'recessed_light':
          return { width: 0.5, height: 0.3, depth: 0.5 }
        case 'outlet':
          return { width: 0.3, height: 0.5, depth: 0.1 }
        
        // Flooring (represented as sample sections)
        case 'hardwood_flooring':
          return { width: 4, height: 0.1, depth: 4 }
        case 'tile_flooring':
          return { width: 4, height: 0.1, depth: 4 }
        case 'carpet':
          return { width: 4, height: 0.1, depth: 4 }
        
        default:
          return { width: 1, height: 1, depth: 1 }
      }
    }

    // Position equipment near the selected ADU
    const aduPosition = selectedObject.position
    const equipmentPosition: [number, number, number] = [
      aduPosition[0] + selectedObject.dimensions.width/2 + 2, // 2 feet from ADU edge
      0.5, // Slightly above ground
      aduPosition[2]
    ]

    const equipmentName = equipmentType.split('_').map((word: string) => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')

    addADUEquipment({
      type: 'ADUEquipment',
      name: `${equipmentName}`,
      equipmentType,
      position: equipmentPosition,
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      dimensions: getEquipmentDimensions(equipmentType),
      materials: {
        primary: '#f7fafc',
        secondary: '#4a5568',
        accent: '#2d3748'
      },
      aduId: selectedObject.id
    })
  }

  // Drag and drop handlers
  const handleDragStart = (equipmentType: ADUEquipmentType | PoolEquipmentType, type: 'adu' | 'pool') => (e: React.DragEvent) => {
    console.log('Drag start:', equipmentType, type)
    setDraggedComponent({ type, equipmentType })
    
    // Set drag data
    e.dataTransfer.setData('text/plain', JSON.stringify({ type, equipmentType }))
    e.dataTransfer.effectAllowed = 'copy'
    
    // Create a custom drag image
    const dragImage = document.createElement('div')
    dragImage.textContent = `📦 ${equipmentType.replace(/_/g, ' ')}`
    dragImage.style.padding = '8px 12px'
    dragImage.style.background = '#1890ff'
    dragImage.style.color = 'white'
    dragImage.style.borderRadius = '6px'
    dragImage.style.fontSize = '12px'
    dragImage.style.position = 'absolute'
    dragImage.style.top = '-1000px'
    document.body.appendChild(dragImage)
    e.dataTransfer.setDragImage(dragImage, 50, 20)
    
    // Clean up drag image after a short delay
    setTimeout(() => {
      document.body.removeChild(dragImage)
    }, 0)
  }

  const handleDragEnd = () => {
    console.log('Drag end')
    setDraggedComponent(null)
  }

  const handleDelete = () => {
    if (selectedObject) {
      deleteObject(selectedObject.id)
    }
  }

  // Modular ADU handlers
  const handleToggleModular = () => {
    if (selectedObject && selectedObject.type === 'ADU') {
      toggleADUModular(selectedObject.id)
    }
  }

  const handleAddSection = (roomType: 'living' | 'kitchen' | 'bedroom' | 'bathroom' | 'dining' | 'office' | 'storage' | 'utility') => {
    if (selectedObject && selectedObject.type === 'ADU') {
      const adu = selectedObject as ADUModel
      const sectionCount = adu.sections.length
      
      // Position new section adjacent to existing ones
      const basePosition: [number, number, number] = [
        sectionCount * 12, // Offset by 12 feet
        0,
        0
      ]
      
      addADUSection(selectedObject.id, {
        name: `${roomType.charAt(0).toUpperCase() + roomType.slice(1)} Section`,
        position: basePosition,
        dimensions: { width: 12, height: 9, depth: 16 },
        materials: adu.materials,
        features: { bedrooms: 0, bathrooms: 0, kitchen: false, livingRoom: false, dining: false, office: false, storage: false, laundry: false, parking: false, porch: false, balcony: false, fireplace: false },
        roomType
      })
    }
  }

  const handleDeleteSection = (sectionId: string) => {
    if (selectedObject && selectedObject.type === 'ADU') {
      deleteADUSection(selectedObject.id, sectionId)
    }
  }

  return (
    <Card
      title={
        <Space>
          <SettingOutlined />
          <Title level={4} style={{ margin: 0 }}>Design Tools</Title>
        </Space>
      }
      style={{ width: 350, maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}
      extra={
        <Space>
          <Button size="small" onClick={undo} title="Undo">
            <UndoOutlined />
          </Button>
          <Button size="small" onClick={redo} title="Redo">
            <RedoOutlined />
          </Button>
          <Button size="small" onClick={onClose} title="Close Panel">
            <CloseOutlined />
          </Button>
        </Space>
      }
    >
      {!selectedObject ? (
        <Alert
          message="No Object Selected"
          description="Click on an ADU or Pool in the 3D scene to edit its properties."
          type="info"
          showIcon
          icon={<HomeOutlined />}
        />
      ) : (
        <div>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Object Info */}
            <div>
              <Title level={5}>{selectedObject.type} Properties</Title>
              <Text type="secondary">Name: {selectedObject.name}</Text>
              
              {/* Debug info */}
              <div style={{ fontSize: '12px', color: '#999', marginTop: '8px', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                <div><strong>🎯 Input Values (Local):</strong> {JSON.stringify(localDimensions)}</div>
                <div><strong>🏠 3D Model Values (Store):</strong> {JSON.stringify(selectedObject?.dimensions)}</div>
                <div><strong>📍 Position:</strong> [{selectedObject.position[0].toFixed(1)}, {selectedObject.position[1].toFixed(1)}, {selectedObject.position[2].toFixed(1)}]</div>
                <div><strong>🔄 Rotation:</strong> [{(selectedObject.rotation[0] * 180 / Math.PI).toFixed(0)}°, {(selectedObject.rotation[1] * 180 / Math.PI).toFixed(0)}°, {(selectedObject.rotation[2] * 180 / Math.PI).toFixed(0)}°]</div>
                <div><strong>📏 Scale:</strong> {selectedObject.scale[0].toFixed(2)}</div>
                <div style={{ color: '#52c41a', marginTop: '4px' }}>
                  ✅ Sliders should reflect these values!
                </div>
              </div>
            </div>

            {/* Transform Controls */}
            <Collapse size="small" defaultActiveKey={['position']}>
              <Panel header="Position" key="position">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text>X: {selectedObject.position[0].toFixed(1)}</Text>
                    <Slider
                      key={`position-x-${selectedObject.id}`}
                      min={-25}
                      max={25}
                      step={0.5}
                      value={selectedObject.position[0]}
                      onChange={(value) => handlePropertyChange('positionX', value)}
                    />
                  </div>
                  <div>
                    <Text>Y: {selectedObject.position[1].toFixed(1)}</Text>
                    <Slider
                      key={`position-y-${selectedObject.id}`}
                      min={-5}
                      max={10}
                      step={0.1}
                      value={selectedObject.position[1]}
                      onChange={(value) => handlePropertyChange('positionY', value)}
                    />
                  </div>
                  <div>
                    <Text>Z: {selectedObject.position[2].toFixed(1)}</Text>
                    <Slider
                      key={`position-z-${selectedObject.id}`}
                      min={-25}
                      max={25}
                      step={0.5}
                      value={selectedObject.position[2]}
                      onChange={(value) => handlePropertyChange('positionZ', value)}
                    />
                  </div>
                </Space>
              </Panel>

              <Panel header="Rotation" key="rotation">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text>X: {(selectedObject.rotation[0] * 180 / Math.PI).toFixed(0)}°</Text>
                    <Slider
                      key={`rotation-x-${selectedObject.id}`}
                      min={0}
                      max={360}
                      value={selectedObject.rotation[0] * 180 / Math.PI}
                      onChange={(value) => handlePropertyChange('rotationX', value * Math.PI / 180)}
                    />
                  </div>
                  <div>
                    <Text>Y: {(selectedObject.rotation[1] * 180 / Math.PI).toFixed(0)}°</Text>
                    <Slider
                      key={`rotation-y-${selectedObject.id}`}
                      min={0}
                      max={360}
                      value={selectedObject.rotation[1] * 180 / Math.PI}
                      onChange={(value) => handlePropertyChange('rotationY', value * Math.PI / 180)}
                    />
                  </div>
                  <div>
                    <Text>Z: {(selectedObject.rotation[2] * 180 / Math.PI).toFixed(0)}°</Text>
                    <Slider
                      key={`rotation-z-${selectedObject.id}`}
                      min={0}
                      max={360}
                      value={selectedObject.rotation[2] * 180 / Math.PI}
                      onChange={(value) => handlePropertyChange('rotationZ', value * Math.PI / 180)}
                    />
                  </div>
                </Space>
              </Panel>

              <Panel header="Scale" key="scale">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text>Uniform Scale: {selectedObject.scale[0].toFixed(2)}</Text>
                    <Slider
                      key={`scale-${selectedObject.id}`}
                      min={0.5}
                      max={3}
                      step={0.1}
                      value={selectedObject.scale[0]}
                      onChange={(value) => {
                        handlePropertyChange('scaleX', value)
                        handlePropertyChange('scaleY', value)
                        handlePropertyChange('scaleZ', value)
                      }}
                    />
                  </div>
                </Space>
              </Panel>
            </Collapse>

            {/* Type-specific controls */}
            {selectedObject.type === 'ADU' && (
              <Collapse size="small">
                <Panel header="ADU Templates" key="templates">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Button 
                      type="primary" 
                      onClick={() => setShowTemplateModal(true)} 
                      block
                    >
                      Choose Template
                    </Button>
                    {(selectedObject as ADUModel).templateId && (
                      <Alert
                        message="Template Applied"
                        description={ADU_TEMPLATES.find(t => t.id === (selectedObject as ADUModel).templateId)?.name || 'Custom'}
                        type="success"
                        showIcon
                      />
                    )}
                  </Space>
                </Panel>
                
                <Panel header="ADU Dimensions" key="dimensions">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text>Width: </Text>
                      <InputNumber
                        min={5}
                        max={30}
                        value={localDimensions?.width ?? (selectedObject as ADUModel).dimensions.width}
                        onChange={(value) => handleDimensionChange('width', value || 10)}
                      />
                    </div>
                    <div>
                      <Text>Height: </Text>
                      <InputNumber
                        min={5}
                        max={15}
                        value={localDimensions?.height ?? (selectedObject as ADUModel).dimensions.height}
                        onChange={(value) => handleDimensionChange('height', value || 8)}
                      />
                    </div>
                    <div>
                      <Text>Depth: </Text>
                      <InputNumber
                        min={5}
                        max={30}
                        value={localDimensions?.depth ?? (selectedObject as ADUModel).dimensions.depth}
                        onChange={(value) => handleDimensionChange('depth', value || 12)}
                      />
                    </div>
                  </Space>
                </Panel>

                <Panel header="ADU Materials" key="materials">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Choose materials and finishes. Higher quality materials increase cost but improve durability and aesthetics.
                    </Text>

                    <Divider orientation="left" style={{ margin: '8px 0' }}>🏠 Exterior Walls</Divider>
                    <div>
                      <Text>Wall Material:</Text>
                      <Select
                        value={(selectedObject as ADUModel).materials.walls}
                        onChange={(value) => handleMaterialChange('walls', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                        placeholder="Select wall material"
                      >
                        <Select.Option value="#f5f5f5">Standard Siding - $8/sq ft</Select.Option>
                        <Select.Option value="#e2e8f0">Fiber Cement - $12/sq ft</Select.Option>
                        <Select.Option value="#8b4513">Wood Siding - $15/sq ft</Select.Option>
                        <Select.Option value="#654321">Cedar Siding - $18/sq ft</Select.Option>
                        <Select.Option value="#2d3748">Metal Siding - $14/sq ft</Select.Option>
                        <Select.Option value="#4a5568">Brick Veneer - $22/sq ft</Select.Option>
                        <Select.Option value="#1a202c">Stone Veneer - $28/sq ft</Select.Option>
                      </Select>
                    </div>

                    <Divider orientation="left" style={{ margin: '8px 0' }}>🏠 Roofing</Divider>
                    <div>
                      <Text>Roof Material:</Text>
                      <Select
                        value={(selectedObject as ADUModel).materials.roof}
                        onChange={(value) => handleMaterialChange('roof', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                        placeholder="Select roof material"
                      >
                        <Select.Option value="#808080">Asphalt Shingles - $4/sq ft</Select.Option>
                        <Select.Option value="#696969">Architectural Shingles - $6/sq ft</Select.Option>
                        <Select.Option value="#2f4f4f">Metal Roofing - $8/sq ft</Select.Option>
                        <Select.Option value="#8b4513">Cedar Shakes - $12/sq ft</Select.Option>
                        <Select.Option value="#654321">Slate Tiles - $15/sq ft</Select.Option>
                        <Select.Option value="#4a5568">Clay Tiles - $10/sq ft</Select.Option>
                        <Select.Option value="#1a202c">Premium Metal - $12/sq ft</Select.Option>
                      </Select>
                    </div>

                    <Divider orientation="left" style={{ margin: '8px 0' }}>🚪 Doors & Windows</Divider>
                    <div>
                      <Text>Entry Door:</Text>
                      <Select
                        value={(selectedObject as ADUModel).materials.door}
                        onChange={(value) => handleMaterialChange('door', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                        placeholder="Select door type"
                      >
                        <Select.Option value="#8b4513">Standard Wood - $400</Select.Option>
                        <Select.Option value="#654321">Solid Wood - $800</Select.Option>
                        <Select.Option value="#2f4f4f">Steel Entry - $600</Select.Option>
                        <Select.Option value="#4a5568">Fiberglass - $700</Select.Option>
                        <Select.Option value="#1a202c">Premium Wood - $1,200</Select.Option>
                        <Select.Option value="#744210">Custom Wood - $1,800</Select.Option>
                      </Select>
                    </div>

                    <div>
                      <Text>Windows:</Text>
                      <Select
                        value={(selectedObject as ADUModel).materials.windows}
                        onChange={(value) => handleMaterialChange('windows', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                        placeholder="Select window type"
                      >
                        <Select.Option value="#87ceeb">Standard Glass - $300/window</Select.Option>
                        <Select.Option value="#4169e1">Double Pane - $450/window</Select.Option>
                        <Select.Option value="#1e90ff">Low-E Glass - $600/window</Select.Option>
                        <Select.Option value="#0066cc">Triple Pane - $800/window</Select.Option>
                        <Select.Option value="#003d7a">Premium Low-E - $1,000/window</Select.Option>
                      </Select>
                    </div>

                    <Divider orientation="left" style={{ margin: '8px 0' }}>🎨 Trim & Accents</Divider>
                    <div>
                      <Text>Trim Material:</Text>
                      <Select
                        value={(selectedObject as ADUModel).materials.trim}
                        onChange={(value) => handleMaterialChange('trim', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                        placeholder="Select trim material"
                      >
                        <Select.Option value="#ffffff">Vinyl Trim - $3/linear ft</Select.Option>
                        <Select.Option value="#f5f5f5">Composite Trim - $5/linear ft</Select.Option>
                        <Select.Option value="#e2e8f0">Wood Trim - $7/linear ft</Select.Option>
                        <Select.Option value="#d1d5db">Cedar Trim - $9/linear ft</Select.Option>
                        <Select.Option value="#9ca3af">Aluminum Trim - $6/linear ft</Select.Option>
                      </Select>
                    </div>

                    <Alert
                      message="💰 Material Cost Impact"
                      description="Premium materials can increase total project cost by 15-40% but provide better durability, energy efficiency, and resale value."
                      type="info"
                      showIcon
                      style={{ fontSize: '11px', marginTop: '12px' }}
                    />
                  </Space>
                </Panel>

                <Panel header="ADU Features" key="features">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text>Bedrooms: </Text>
                      <InputNumber
                        min={0}
                        max={5}
                        value={(selectedObject as ADUModel).features.bedrooms}
                        onChange={(value) => handleFeatureChange('bedrooms', value || 1)}
                      />
                    </div>
                    <div>
                      <Text>Bathrooms: </Text>
                      <InputNumber
                        min={0}
                        max={3}
                        step={0.5}
                        value={(selectedObject as ADUModel).features.bathrooms}
                        onChange={(value) => handleFeatureChange('bathrooms', value || 1)}
                      />
                    </div>
                    <div>
                      <Text>Kitchen: </Text>
                      <Switch
                        checked={(selectedObject as ADUModel).features.kitchen}
                        onChange={(value) => handleFeatureChange('kitchen', value)}
                      />
                    </div>
                    <div>
                      <Text>Living Room: </Text>
                      <Switch
                        checked={(selectedObject as ADUModel).features.livingRoom}
                        onChange={(value) => handleFeatureChange('livingRoom', value)}
                      />
                    </div>
                  </Space>
                </Panel>

                <Panel header="Modular Design" key="modular">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Text>Enable Modular Design:</Text>
                      <Switch
                        key={`modular-${selectedObject.id}`}
                        checked={(selectedObject as ADUModel).isModular || false}
                        onChange={handleToggleModular}
                      />
                    </div>
                    
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Modular design lets you combine multiple sections to create custom ADU shapes like L-shaped or T-shaped buildings.
                    </Text>

                    {(selectedObject as ADUModel).isModular && (
                      <>
                        <Divider orientation="left" style={{ margin: '8px 0' }}>Add Sections</Divider>
                        <Space wrap>
                          <Button 
                            size="small" 
                            onClick={() => handleAddSection('living')}
                            style={{ background: '#48bb78', color: 'white', border: 'none' }}
                          >
                            + Living Room
                          </Button>
                          <Button 
                            size="small" 
                            onClick={() => handleAddSection('kitchen')}
                            style={{ background: '#ed8936', color: 'white', border: 'none' }}
                          >
                            + Kitchen
                          </Button>
                          <Button 
                            size="small" 
                            onClick={() => handleAddSection('bedroom')}
                            style={{ background: '#4299e1', color: 'white', border: 'none' }}
                          >
                            + Bedroom
                          </Button>
                          <Button 
                            size="small" 
                            onClick={() => handleAddSection('bathroom')}
                            style={{ background: '#9f7aea', color: 'white', border: 'none' }}
                          >
                            + Bathroom
                          </Button>
                          <Button 
                            size="small" 
                            onClick={() => handleAddSection('dining')}
                            style={{ background: '#38b2ac', color: 'white', border: 'none' }}
                          >
                            + Dining
                          </Button>
                          <Button 
                            size="small" 
                            onClick={() => handleAddSection('office')}
                            style={{ background: '#f56565', color: 'white', border: 'none' }}
                          >
                            + Office
                          </Button>
                          <Button 
                            size="small" 
                            onClick={() => handleAddSection('storage')}
                            style={{ background: '#a0aec0', color: 'white', border: 'none' }}
                          >
                            + Storage
                          </Button>
                        </Space>

                        {(selectedObject as ADUModel).sections.length > 0 && (
                          <>
                            <Divider orientation="left" style={{ margin: '8px 0' }}>Current Sections</Divider>
                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                              {(selectedObject as ADUModel).sections.map((section: any) => (
                                <div 
                                  key={section.id} 
                                  style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    padding: '8px 12px',
                                    background: '#f5f5f5',
                                    borderRadius: '6px',
                                    marginBottom: '6px',
                                    border: '1px solid #e2e8f0'
                                  }}
                                >
                                  <div>
                                    <Text style={{ fontSize: '13px', fontWeight: 'bold' }}>{section.name}</Text>
                                    <br />
                                    <Text style={{ fontSize: '11px', color: '#666' }}>
                                      {section.dimensions.width}' × {section.dimensions.depth}' × {section.dimensions.height}'
                                    </Text>
                                  </div>
                                  <Button 
                                    size="small" 
                                    type="text" 
                                    danger
                                    onClick={() => handleDeleteSection(section.id)}
                                    style={{ fontSize: '10px', padding: '0 6px' }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </>
                        )}

                        <Alert
                          message="🏗️ Modular Design Tips"
                          description="Each section can be dragged independently. Use different room types to create complex layouts. Sections automatically connect when placed adjacent to each other."
                          type="info"
                          showIcon
                          style={{ fontSize: '11px' }}
                        />
                      </>
                    )}
                  </Space>
                </Panel>

                <Panel header="ADU Building Components" key="components">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Add individual building components for accurate construction quotes
                    </Text>
                    
                    <Alert
                      message="💡 Interior View Tip"
                      description="Click 'Interior View' button in the header to see room layouts and drop components in the right rooms!"
                      type="info"
                      showIcon
                      style={{ fontSize: '11px', marginTop: '8px' }}
                    />
                    
                    <Divider orientation="left" style={{ margin: '8px 0' }}>🚿 Plumbing Fixtures</Divider>
                    <Space wrap>
                      <DraggableEquipmentButton
                        equipmentType="toilet"
                        componentType="adu"
                        label="Add Toilet"
                        onClick={() => handleAddADUEquipment('toilet')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="sink_bathroom"
                        componentType="adu"
                        label="Bath Sink"
                        onClick={() => handleAddADUEquipment('sink_bathroom')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="sink_kitchen"
                        componentType="adu"
                        label="Kitchen Sink"
                        onClick={() => handleAddADUEquipment('sink_kitchen')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="shower"
                        componentType="adu"
                        label="Add Shower"
                        onClick={() => handleAddADUEquipment('shower')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="bathtub"
                        componentType="adu"
                        label="Add Bathtub"
                        onClick={() => handleAddADUEquipment('bathtub')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                    </Space>

                    <Divider orientation="left" style={{ margin: '8px 0' }}>🍳 Kitchen Appliances</Divider>
                    <Space wrap>
                      <DraggableEquipmentButton
                        equipmentType="refrigerator"
                        componentType="adu"
                        label="Refrigerator"
                        onClick={() => handleAddADUEquipment('refrigerator')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="stove"
                        componentType="adu"
                        label="Stove/Range"
                        onClick={() => handleAddADUEquipment('stove')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="dishwasher"
                        componentType="adu"
                        label="Dishwasher"
                        onClick={() => handleAddADUEquipment('dishwasher')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="microwave"
                        componentType="adu"
                        label="Microwave"
                        onClick={() => handleAddADUEquipment('microwave')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                    </Space>

                    <Divider orientation="left" style={{ margin: '8px 0' }}>🗄️ Cabinets & Storage</Divider>
                    <Space wrap>
                      <DraggableEquipmentButton
                        equipmentType="kitchen_cabinets"
                        componentType="adu"
                        label="Kitchen Cabinets"
                        onClick={() => handleAddADUEquipment('kitchen_cabinets')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="bathroom_vanity"
                        componentType="adu"
                        label="Bath Vanity"
                        onClick={() => handleAddADUEquipment('bathroom_vanity')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="closet_system"
                        componentType="adu"
                        label="Closet System"
                        onClick={() => handleAddADUEquipment('closet_system')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                    </Space>

                    <Divider orientation="left" style={{ margin: '8px 0' }}>🚪 Windows & Doors</Divider>
                    <Space wrap>
                      <DraggableEquipmentButton
                        equipmentType="window_standard"
                        componentType="adu"
                        label="Standard Window"
                        onClick={() => handleAddADUEquipment('window_standard')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="window_large"
                        componentType="adu"
                        label="Large Window"
                        onClick={() => handleAddADUEquipment('window_large')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="entry_door"
                        componentType="adu"
                        label="Entry Door"
                        onClick={() => handleAddADUEquipment('entry_door')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="interior_door"
                        componentType="adu"
                        label="Interior Door"
                        onClick={() => handleAddADUEquipment('interior_door')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                    </Space>

                    <Divider orientation="left" style={{ margin: '8px 0' }}>⚡ HVAC & Utilities</Divider>
                    <Space wrap>
                      <DraggableEquipmentButton
                        equipmentType="hvac_unit"
                        componentType="adu"
                        label="HVAC Unit"
                        onClick={() => handleAddADUEquipment('hvac_unit')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="electrical_panel"
                        componentType="adu"
                        label="Electrical Panel"
                        onClick={() => handleAddADUEquipment('electrical_panel')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="water_heater"
                        componentType="adu"
                        label="Water Heater"
                        onClick={() => handleAddADUEquipment('water_heater')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                    </Space>

                    <Divider orientation="left" style={{ margin: '8px 0' }}>💡 Lighting & Electrical</Divider>
                    <Space wrap>
                      <DraggableEquipmentButton
                        equipmentType="ceiling_light"
                        componentType="adu"
                        label="Ceiling Light"
                        onClick={() => handleAddADUEquipment('ceiling_light')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="recessed_light"
                        componentType="adu"
                        label="Recessed Light"
                        onClick={() => handleAddADUEquipment('recessed_light')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                      <DraggableEquipmentButton
                        equipmentType="outlet"
                        componentType="adu"
                        label="Add Outlet"
                        onClick={() => handleAddADUEquipment('outlet')}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggedComponent={draggedComponent}
                      />
                    </Space>

                    {/* Show existing components for this ADU */}
                    {(() => {
                      const aduComponents = getADUEquipmentForADU(selectedObject.id)
                      return aduComponents.length > 0 && (
                        <>
                          <Divider orientation="left" style={{ margin: '8px 0' }}>📦 Current Components</Divider>
                          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {aduComponents.map((component: ADUEquipmentModel) => (
                              <div 
                                key={component.id} 
                                style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '4px 8px',
                                  background: '#f5f5f5',
                                  borderRadius: '4px',
                                  marginBottom: '4px'
                                }}
                              >
                                <Text style={{ fontSize: '12px' }}>{component.name}</Text>
                                <Button 
                                  size="small" 
                                  type="text" 
                                  danger
                                  onClick={() => deleteObject(component.id)}
                                  style={{ fontSize: '10px', padding: '0 4px' }}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        </>
                      )
                    })()}

                    <Alert
                      message="💡 Pro Tip"
                      description="Building components can be dragged and positioned individually. This helps create accurate material lists and construction quotes."
                      type="info"
                      showIcon
                      style={{ fontSize: '11px' }}
                    />
                  </Space>
                </Panel>
              </Collapse>
            )}

            {selectedObject.type === 'Pool' && (
              <Collapse size="small">
                <Panel header="Pool Shape" key="shape">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text>Shape:</Text>
                      <Select
                        value={(selectedObject as PoolModel).shape}
                        onChange={handleShapeChange}
                        style={{ width: '100%', marginTop: '8px' }}
                      >
                        <Select.Option value="rectangular">🔲 Rectangular</Select.Option>
                        <Select.Option value="oval">🥚 Oval</Select.Option>
                        <Select.Option value="round">⭕ Round</Select.Option>
                        <Select.Option value="kidney">🫘 Kidney</Select.Option>
                        <Select.Option value="l-shape">📐 L-Shape</Select.Option>
                        <Select.Option value="freeform">🌊 Freeform</Select.Option>
                      </Select>
                    </div>
                  </Space>
                </Panel>
                
                <Panel header="Pool Dimensions" key="dimensions">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {/* Shape-specific dimension controls */}
                    {(selectedObject as PoolModel).shape === 'round' ? (
                      // Round pool: Only diameter and depth
                      <>
                        <div>
                          <Text>Diameter: </Text>
                          <InputNumber
                            min={5}
                            max={40}
                            value={localDimensions?.width ?? (selectedObject as PoolModel).dimensions.width}
                            onChange={(value) => {
                              // For round pools, set both width and length to the same value
                              handleDimensionChange('width', value || 15)
                              handleDimensionChange('length', value || 15)
                            }}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                        <div>
                          <Text>Depth: </Text>
                          <InputNumber
                            min={3}
                            max={12}
                            value={localDimensions?.depth ?? (selectedObject as PoolModel).dimensions.depth}
                            onChange={(value) => handleDimensionChange('depth', value || 8)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                      </>
                    ) : (selectedObject as PoolModel).shape === 'oval' ? (
                      // Oval pool: Width (minor axis) and Length (major axis)
                      <>
                        <div>
                          <Text>Width (Minor Axis): </Text>
                          <InputNumber
                            min={5}
                            max={30}
                            value={localDimensions?.width ?? (selectedObject as PoolModel).dimensions.width}
                            onChange={(value) => handleDimensionChange('width', value || 15)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                        <div>
                          <Text>Length (Major Axis): </Text>
                          <InputNumber
                            min={5}
                            max={40}
                            value={localDimensions?.length ?? (selectedObject as PoolModel).dimensions.length}
                            onChange={(value) => handleDimensionChange('length', value || 20)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                        <div>
                          <Text>Depth: </Text>
                          <InputNumber
                            min={3}
                            max={12}
                            value={localDimensions?.depth ?? (selectedObject as PoolModel).dimensions.depth}
                            onChange={(value) => handleDimensionChange('depth', value || 8)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                      </>
                    ) : (selectedObject as PoolModel).shape === 'kidney' ? (
                      // Kidney pool: Overall width, length, and depth
                      <>
                        <div>
                          <Text>Overall Width: </Text>
                          <InputNumber
                            min={8}
                            max={35}
                            value={localDimensions?.width ?? (selectedObject as PoolModel).dimensions.width}
                            onChange={(value) => handleDimensionChange('width', value || 18)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                        <div>
                          <Text>Overall Length: </Text>
                          <InputNumber
                            min={10}
                            max={45}
                            value={localDimensions?.length ?? (selectedObject as PoolModel).dimensions.length}
                            onChange={(value) => handleDimensionChange('length', value || 25)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                        <div>
                          <Text>Depth: </Text>
                          <InputNumber
                            min={3}
                            max={12}
                            value={localDimensions?.depth ?? (selectedObject as PoolModel).dimensions.depth}
                            onChange={(value) => handleDimensionChange('depth', value || 8)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                      </>
                    ) : (selectedObject as PoolModel).shape === 'l-shape' ? (
                      // L-shape pool: Main section and side section dimensions
                      <>
                        <div>
                          <Text>Main Section Width: </Text>
                          <InputNumber
                            min={8}
                            max={30}
                            value={localDimensions?.width ?? (selectedObject as PoolModel).dimensions.width}
                            onChange={(value) => handleDimensionChange('width', value || 20)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                        <div>
                          <Text>Total Length: </Text>
                          <InputNumber
                            min={12}
                            max={45}
                            value={localDimensions?.length ?? (selectedObject as PoolModel).dimensions.length}
                            onChange={(value) => handleDimensionChange('length', value || 30)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                        <div>
                          <Text>Depth: </Text>
                          <InputNumber
                            min={3}
                            max={12}
                            value={localDimensions?.depth ?? (selectedObject as PoolModel).dimensions.depth}
                            onChange={(value) => handleDimensionChange('depth', value || 8)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                      </>
                    ) : (
                      // Default (rectangular, freeform): Standard width, length, depth
                      <>
                        <div>
                          <Text>Width: </Text>
                          <InputNumber
                            min={5}
                            max={30}
                            value={localDimensions?.width ?? (selectedObject as PoolModel).dimensions.width}
                            onChange={(value) => handleDimensionChange('width', value || 15)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                        <div>
                          <Text>Length: </Text>
                          <InputNumber
                            min={5}
                            max={40}
                            value={localDimensions?.length ?? (selectedObject as PoolModel).dimensions.length}
                            onChange={(value) => handleDimensionChange('length', value || 20)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                        <div>
                          <Text>Depth: </Text>
                          <InputNumber
                            min={3}
                            max={12}
                            value={localDimensions?.depth ?? (selectedObject as PoolModel).dimensions.depth}
                            onChange={(value) => handleDimensionChange('depth', value || 8)}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px' }}>ft</Text>
                        </div>
                      </>
                    )}
                    
                    {/* Shape-specific info */}
                    <div style={{ marginTop: '12px', padding: '8px', background: '#f0f8ff', borderRadius: '4px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {(selectedObject as PoolModel).shape === 'round' && '⭕ Round pools use diameter for both width and length'}
                        {(selectedObject as PoolModel).shape === 'oval' && '🥚 Oval pools: Width = short side, Length = long side'}
                        {(selectedObject as PoolModel).shape === 'kidney' && '🫘 Kidney pools: Organic curved shape with overall dimensions'}
                        {(selectedObject as PoolModel).shape === 'l-shape' && '📐 L-shaped pools: Main section + side extension'}
                        {(selectedObject as PoolModel).shape === 'rectangular' && '🔲 Rectangular pools: Standard width × length'}
                        {(selectedObject as PoolModel).shape === 'freeform' && '🌊 Freeform pools: Organic shape within overall dimensions'}
                      </Text>
                    </div>
                  </Space>
                </Panel>

                <Panel header="Pool Materials" key="materials">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {/* Shell Construction */}
                    <div>
                      <Text strong>Shell Construction:</Text>
                      <Select
                        value={(selectedObject as PoolModel).materials.shell}
                        onChange={(value) => handleMaterialChange('shell', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                      >
                        <Select.Option value="gunite">🏗️ Gunite (Good) - $45-55/sq ft</Select.Option>
                        <Select.Option value="concrete">🧱 Concrete (Better) - $50-65/sq ft</Select.Option>
                        <Select.Option value="fiberglass">🛡️ Fiberglass (Best) - $60-80/sq ft</Select.Option>
                        <Select.Option value="vinyl">💧 Vinyl Liner - $35-45/sq ft</Select.Option>
                      </Select>
                    </div>

                    {/* Interior Finish */}
                    <div>
                      <Text strong>Interior Finish:</Text>
                      <Select
                        value={(selectedObject as PoolModel).materials.interior}
                        onChange={(value) => handleMaterialChange('interior', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                      >
                        <Select.Option value="plaster">🎨 White Plaster (Good) - $3-5/sq ft</Select.Option>
                        <Select.Option value="pebble">🪨 Pebble Tec (Better) - $8-12/sq ft</Select.Option>
                        <Select.Option value="quartz">💎 Quartz (Better) - $10-15/sq ft</Select.Option>
                        <Select.Option value="tile">🏺 Full Tile (Best) - $15-25/sq ft</Select.Option>
                        <Select.Option value="glass_bead">✨ Glass Bead (Best) - $12-18/sq ft</Select.Option>
                      </Select>
                    </div>

                    <div>
                      <Text>Interior Color: </Text>
                      <ColorPicker 
                        value={(selectedObject as PoolModel).materials.interiorColor} 
                        onChange={(color) => handleColorChange('interiorColor', color)} 
                      />
                    </div>

                    {/* Coping */}
                    <div>
                      <Text strong>Coping:</Text>
                      <Select
                        value={(selectedObject as PoolModel).materials.coping}
                        onChange={(value) => handleMaterialChange('coping', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                      >
                        <Select.Option value="concrete">🏗️ Concrete (Good) - $8-12/lin ft</Select.Option>
                        <Select.Option value="brick">🧱 Brick (Better) - $12-18/lin ft</Select.Option>
                        <Select.Option value="travertine">🏛️ Travertine (Better) - $15-25/lin ft</Select.Option>
                        <Select.Option value="natural_stone">🪨 Natural Stone (Best) - $20-35/lin ft</Select.Option>
                        <Select.Option value="granite">💎 Granite (Best) - $25-40/lin ft</Select.Option>
                      </Select>
                    </div>

                    <div>
                      <Text>Coping Color: </Text>
                      <ColorPicker 
                        value={(selectedObject as PoolModel).materials.copingColor} 
                        onChange={(color) => handleColorChange('copingColor', color)} 
                      />
                    </div>

                    {/* Decking */}
                    <div>
                      <Text strong>Decking:</Text>
                      <Select
                        value={(selectedObject as PoolModel).materials.deck}
                        onChange={(value) => handleMaterialChange('deck', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                      >
                        <Select.Option value="concrete">🏗️ Concrete (Good) - $6-10/sq ft</Select.Option>
                        <Select.Option value="pavers">🧱 Pavers (Better) - $12-20/sq ft</Select.Option>
                        <Select.Option value="natural_stone">🪨 Natural Stone (Best) - $18-30/sq ft</Select.Option>
                        <Select.Option value="wood">🌳 Wood Decking - $15-25/sq ft</Select.Option>
                        <Select.Option value="composite">♻️ Composite - $20-35/sq ft</Select.Option>
                      </Select>
                    </div>

                    <div>
                      <Text>Deck Color: </Text>
                      <ColorPicker 
                        value={(selectedObject as PoolModel).materials.deckColor} 
                        onChange={(color) => handleColorChange('deckColor', color)} 
                      />
                    </div>

                    {/* Water Color */}
                    <div>
                      <Text>Water Color: </Text>
                      <ColorPicker 
                        value={(selectedObject as PoolModel).materials.water} 
                        onChange={(color) => handleColorChange('water', color)} 
                      />
                    </div>
                  </Space>
                </Panel>

                <Panel header="Pool Equipment & Features" key="features">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    
                    {/* Water Features */}
                    <Divider orientation="left" style={{ margin: '8px 0' }}>💧 Water Features</Divider>
                    <div>
                      <Text>Spa/Hot Tub: </Text>
                      <Switch 
                        checked={(selectedObject as PoolModel).features.spa}
                        onChange={(checked) => handleFeatureChange('spa', checked)}
                      />
                      <Text type="secondary" style={{ marginLeft: '8px' }}>+$15,000-25,000</Text>
                    </div>
                    <div>
                      <Text>Waterfall: </Text>
                      <Switch 
                        checked={(selectedObject as PoolModel).features.waterfall}
                        onChange={(checked) => handleFeatureChange('waterfall', checked)}
                      />
                      <Text type="secondary" style={{ marginLeft: '8px' }}>+$3,000-8,000</Text>
                    </div>
                    <div>
                      <Text>Fountains: </Text>
                      <Switch 
                        checked={(selectedObject as PoolModel).features.fountains}
                        onChange={(checked) => handleFeatureChange('fountains', checked)}
                      />
                      <Text type="secondary" style={{ marginLeft: '8px' }}>+$2,000-5,000</Text>
                    </div>

                    {/* Lighting */}
                    <Divider orientation="left" style={{ margin: '8px 0' }}>💡 Lighting</Divider>
                    <div>
                      <Text>Pool Lighting: </Text>
                      <Switch 
                        checked={(selectedObject as PoolModel).features.lighting}
                        onChange={(checked) => handleFeatureChange('lighting', checked)}
                      />
                    </div>
                    {(selectedObject as PoolModel).features.lighting && (
                      <>
                        <div>
                          <Text>Lighting Type:</Text>
                          <Select
                            value={(selectedObject as PoolModel).features.lightingType}
                            onChange={(value) => handleFeatureChange('lightingType', value)}
                            style={{ width: '100%', marginTop: '4px' }}
                          >
                            <Select.Option value="led">💡 LED (Best) - $800-1,500</Select.Option>
                            <Select.Option value="halogen">🔆 Halogen (Good) - $300-600</Select.Option>
                            <Select.Option value="fiber_optic">✨ Fiber Optic (Better) - $1,200-2,000</Select.Option>
                          </Select>
                        </div>
                        <div>
                          <Text>Light Colors:</Text>
                          <Select
                            value={(selectedObject as PoolModel).features.lightingColor}
                            onChange={(value) => handleFeatureChange('lightingColor', value)}
                            style={{ width: '100%', marginTop: '4px' }}
                          >
                            <Select.Option value="white">⚪ White Only</Select.Option>
                            <Select.Option value="color_changing">🌈 Color Changing +$200-400</Select.Option>
                            <Select.Option value="rgb">🎨 Full RGB +$400-800</Select.Option>
                          </Select>
                        </div>
                      </>
                    )}

                    {/* Heating & Cooling */}
                    <Divider orientation="left" style={{ margin: '8px 0' }}>🔥 Heating & Cooling</Divider>
                    <div>
                      <Text>Pool Heating: </Text>
                      <Switch 
                        checked={(selectedObject as PoolModel).features.heating}
                        onChange={(checked) => handleFeatureChange('heating', checked)}
                      />
                    </div>
                    {(selectedObject as PoolModel).features.heating && (
                      <div>
                        <Text>Heating Type:</Text>
                        <Select
                          value={(selectedObject as PoolModel).features.heatingType}
                          onChange={(value) => handleFeatureChange('heatingType', value)}
                          style={{ width: '100%', marginTop: '4px' }}
                        >
                          <Select.Option value="gas">🔥 Gas Heater (Good) - $3,000-5,000</Select.Option>
                          <Select.Option value="heat_pump">⚡ Heat Pump (Better) - $4,000-7,000</Select.Option>
                          <Select.Option value="solar">☀️ Solar (Best) - $5,000-10,000</Select.Option>
                          <Select.Option value="electric">🔌 Electric - $2,500-4,000</Select.Option>
                        </Select>
                      </div>
                    )}

                    {/* Filtration & Cleaning */}
                    <Divider orientation="left" style={{ margin: '8px 0' }}>🔄 Filtration & Cleaning</Divider>
                    <div>
                      <Text>Filtration System:</Text>
                      <Select
                        value={(selectedObject as PoolModel).features.filtration}
                        onChange={(value) => handleFeatureChange('filtration', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                      >
                        <Select.Option value="sand">🏖️ Sand Filter (Good) - $800-1,200</Select.Option>
                        <Select.Option value="cartridge">📄 Cartridge (Better) - $600-1,000</Select.Option>
                        <Select.Option value="de">💎 DE Filter (Best) - $1,200-2,000</Select.Option>
                      </Select>
                    </div>
                    <div>
                      <Text>Automation System:</Text>
                      <Select
                        value={(selectedObject as PoolModel).features.automation}
                        onChange={(value) => handleFeatureChange('automation', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                      >
                        <Select.Option value="none">🚫 Manual Controls</Select.Option>
                        <Select.Option value="basic">⚙️ Basic Automation (Good) - $1,500-2,500</Select.Option>
                        <Select.Option value="advanced">🎛️ Advanced System (Better) - $3,000-5,000</Select.Option>
                        <Select.Option value="smart_home">📱 Smart Home Integration (Best) - $5,000-8,000</Select.Option>
                      </Select>
                    </div>

                    {/* Water Treatment */}
                    <Divider orientation="left" style={{ margin: '8px 0' }}>🧪 Water Treatment</Divider>
                    <div>
                      <Text>Sanitization System:</Text>
                      <Select
                        value={(selectedObject as PoolModel).features.sanitization}
                        onChange={(value) => handleFeatureChange('sanitization', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                      >
                        <Select.Option value="chlorine">🧪 Traditional Chlorine (Good)</Select.Option>
                        <Select.Option value="salt_water">🧂 Salt Water System (Better) - +$2,000-4,000</Select.Option>
                        <Select.Option value="uv">☀️ UV Sanitization (Best) - +$3,000-5,000</Select.Option>
                        <Select.Option value="ozone">💨 Ozone System (Best) - +$2,500-4,500</Select.Option>
                      </Select>
                    </div>

                    {/* Safety & Covers */}
                    <Divider orientation="left" style={{ margin: '8px 0' }}>🛡️ Safety & Covers</Divider>
                    <div>
                      <Text>Safety Features: </Text>
                      <Switch 
                        checked={(selectedObject as PoolModel).features.safety}
                        onChange={(checked) => handleFeatureChange('safety', checked)}
                      />
                    </div>
                    {(selectedObject as PoolModel).features.safety && (
                      <div>
                        <Text>Safety Type:</Text>
                        <Select
                          value={(selectedObject as PoolModel).features.safetyType}
                          onChange={(value) => handleFeatureChange('safetyType', value)}
                          style={{ width: '100%', marginTop: '4px' }}
                        >
                          <Select.Option value="fence">🚧 Safety Fence - $2,000-4,000</Select.Option>
                          <Select.Option value="cover">🛡️ Safety Cover - $3,000-6,000</Select.Option>
                          <Select.Option value="alarm">🚨 Pool Alarm - $500-1,200</Select.Option>
                          <Select.Option value="all">🔒 Complete Safety Package - $5,000-10,000</Select.Option>
                        </Select>
                      </div>
                    )}

                    {/* Site Conditions */}
                    <Divider orientation="left" style={{ margin: '8px 0' }}>🏗️ Site Conditions</Divider>
                    <div>
                      <Text>Excavation Difficulty:</Text>
                      <Select
                        value={(selectedObject as PoolModel).features.excavation}
                        onChange={(value) => handleFeatureChange('excavation', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                      >
                        <Select.Option value="standard">⚙️ Standard Soil</Select.Option>
                        <Select.Option value="difficult">🪨 Rocky/Clay Soil - +15-25%</Select.Option>
                        <Select.Option value="extreme">⛰️ Extreme Conditions - +30-50%</Select.Option>
                      </Select>
                    </div>
                    <div>
                      <Text>Site Access:</Text>
                      <Select
                        value={(selectedObject as PoolModel).features.access}
                        onChange={(value) => handleFeatureChange('access', value)}
                        style={{ width: '100%', marginTop: '4px' }}
                      >
                        <Select.Option value="easy">🚚 Easy Access</Select.Option>
                        <Select.Option value="moderate">🏠 Moderate Access - +5-10%</Select.Option>
                        <Select.Option value="difficult">🚧 Difficult Access - +15-25%</Select.Option>
                      </Select>
                    </div>
                  </Space>
                </Panel>

                <Panel header="Pool Equipment" key="equipment">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Add individual equipment pieces that can be positioned around your pool
                    </Text>
                    
                    <Divider orientation="left" style={{ margin: '8px 0' }}>⚙️ Essential Equipment</Divider>
                    <Space wrap>
                      <Button 
                        size="small" 
                        onClick={() => handleAddPoolEquipment('pump')}
                        icon={<SettingOutlined />}
                      >
                        Add Pump
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleAddPoolEquipment('filter')}
                        icon={<SettingOutlined />}
                      >
                        Add Filter
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleAddPoolEquipment('electrical_panel')}
                        icon={<SettingOutlined />}
                      >
                        Add Panel
                      </Button>
                    </Space>

                    <Divider orientation="left" style={{ margin: '8px 0' }}>🔥 Optional Equipment</Divider>
                    <Space wrap>
                      <Button 
                        size="small" 
                        onClick={() => handleAddPoolEquipment('heater')}
                        icon={<SettingOutlined />}
                      >
                        Add Heater
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleAddPoolEquipment('chlorinator')}
                        icon={<SettingOutlined />}
                      >
                        Add Chlorinator
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleAddPoolEquipment('automation_controller')}
                        icon={<SettingOutlined />}
                      >
                        Add Controller
                      </Button>
                    </Space>

                    {/* Show existing equipment for this pool */}
                    {(() => {
                      const poolEquipment = getPoolEquipmentForPool(selectedObject.id)
                      return poolEquipment.length > 0 && (
                        <>
                          <Divider orientation="left" style={{ margin: '8px 0' }}>📦 Current Equipment</Divider>
                          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {poolEquipment.map((equipment: PoolEquipmentModel) => (
                              <div 
                                key={equipment.id} 
                                style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '4px 8px',
                                  background: '#f5f5f5',
                                  borderRadius: '4px',
                                  marginBottom: '4px'
                                }}
                              >
                                <Text style={{ fontSize: '12px' }}>{equipment.name}</Text>
                                <Button 
                                  size="small" 
                                  type="text" 
                                  danger
                                  onClick={() => deleteObject(equipment.id)}
                                  style={{ fontSize: '10px', padding: '0 4px' }}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        </>
                      )
                    })()}

                    <Alert
                      message="💡 Pro Tip"
                      description="Equipment can be dragged and positioned individually. Place them at least 3 feet from the pool edge for safety and maintenance access."
                      type="info"
                      showIcon
                      style={{ fontSize: '11px' }}
                    />
                  </Space>
                    </Panel>
                  </Collapse>
                )}

            <Divider />

            {/* Action Buttons */}
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button onClick={clearSelection}>
                Deselect
              </Button>
              <Button type="primary" danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            </Space>
          </Space>
        </div>
      )}
      
      {/* Template Selection Modal */}
      <Modal
        title="Select ADU Template"
        open={showTemplateModal}
        onCancel={() => setShowTemplateModal(false)}
        footer={null}
        width={800}
      >
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 4 }}
          dataSource={ADU_TEMPLATES}
          renderItem={template => (
            <List.Item>
              <Card
                hoverable
                title={template.name}
                onClick={() => handleApplyTemplate(template.id)}
                style={{ 
                  borderColor: selectedObject && (selectedObject as ADUModel).templateId === template.id ? '#1890ff' : undefined 
                }}
              >
                <Typography.Paragraph>
                  <Text strong>Size:</Text> {template.totalSize.width}x{template.totalSize.depth} ft
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <Text strong>Bedrooms:</Text> {template.rooms.bedrooms}
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <Text strong>Bathrooms:</Text> {template.rooms.bathrooms}
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <Text strong>Est. Cost:</Text> ${template.estimatedCost.toLocaleString()}
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <Text strong>Build Time:</Text> {template.buildTime}
                </Typography.Paragraph>
                <Button type="primary" size="small" style={{ marginTop: 10 }}>
                  Select
                </Button>
              </Card>
            </List.Item>
          )}
        />
      </Modal>
    </Card>
  )
}

export default DesignToolsPanel